﻿import JSZip from "jszip";
import { hasDataPage, qs } from "../core/dom";

let base64Files = [];
let activeResult = null;

export function initBase64ImagePage() {
    if (!hasDataPage("base64-image")) return;

    const page = {
        mode: "image-to-base64",

        runBtn: qs("[data-base64-run]"),
        clearBtn: qs("[data-base64-clear]"),
        copyActiveBtn: qs("[data-base64-copy-active]"),
        downloadActiveBtn: qs("[data-base64-download-active]"),
        downloadZipBtn: qs("[data-base64-download-zip]"),

        modeTabs: Array.from(document.querySelectorAll("[data-base64-mode]")),
        imagePanel: qs("[data-image-to-base64-panel]"),
        base64Panel: qs("[data-base64-to-image-panel]"),

        dropzone: qs("[data-base64-dropzone]"),
        fileInput: qs("[data-base64-file-input]"),
        fileList: qs("[data-base64-file-list]"),

        base64Input: qs("[data-base64-input]"),
        pasteSampleBtn: qs("[data-base64-paste-sample]"),
        cleanInputBtn: qs("[data-base64-clean-input]"),

        outputType: qs("[data-base64-output-type]"),
        alt: qs("[data-base64-alt]"),
        width: qs("[data-base64-width]"),
        height: qs("[data-base64-height]"),
        includeMime: qs("[data-base64-include-mime]"),
        wrapLines: qs("[data-base64-wrap-lines]"),

        fallbackMime: qs("[data-base64-fallback-mime]"),
        fileName: qs("[data-base64-file-name]"),

        status: qs("[data-base64-status]"),
        previewImage: qs("[data-base64-preview-image]"),
        previewEmpty: qs("[data-base64-preview-empty]"),

        metaMode: qs("[data-base64-meta-mode]"),
        metaFiles: qs("[data-base64-meta-files]"),
        metaSize: qs("[data-base64-meta-size]"),

        output: qs("[data-base64-output]"),
        copyOutputBtn: qs("[data-base64-copy-output]"),

        outputTabs: Array.from(document.querySelectorAll("[data-base64-output-tab]")),
        outputViews: Array.from(document.querySelectorAll("[data-base64-output-view]")),
        bulkGrid: qs("[data-base64-bulk-grid]")
    };

    bindBase64Events(page);
    updateBase64Meta(page);
}

function bindBase64Events(page) {
    page.modeTabs.forEach(tab => {
        tab.addEventListener("click", () => setBase64Mode(page, tab.dataset.base64Mode));
    });

    page.fileInput?.addEventListener("change", async () => {
        await addFiles(page, Array.from(page.fileInput.files || []));
        page.fileInput.value = "";
    });

    page.dropzone?.addEventListener("dragover", e => {
        e.preventDefault();
        page.dropzone.classList.add("is-dragover");
    });

    page.dropzone?.addEventListener("dragleave", () => {
        page.dropzone.classList.remove("is-dragover");
    });

    page.dropzone?.addEventListener("drop", async e => {
        e.preventDefault();
        page.dropzone.classList.remove("is-dragover");
        await addFiles(page, Array.from(e.dataTransfer.files || []));
    });

    page.runBtn?.addEventListener("click", () => runBase64Conversion(page));
    page.clearBtn?.addEventListener("click", () => clearBase64Tool(page));

    page.copyOutputBtn?.addEventListener("click", () => copyOutput(page));
    page.copyActiveBtn?.addEventListener("click", () => copyOutput(page));
    page.downloadActiveBtn?.addEventListener("click", () => downloadActive(page));
    page.downloadZipBtn?.addEventListener("click", () => downloadZip(page));

    page.pasteSampleBtn?.addEventListener("click", () => loadBase64Sample(page));
    page.cleanInputBtn?.addEventListener("click", () => cleanBase64Input(page));

    const liveInputs = [
        page.outputType,
        page.alt,
        page.width,
        page.height,
        page.includeMime,
        page.wrapLines,
        page.fallbackMime,
        page.fileName
    ].filter(Boolean);

    liveInputs.forEach(input => {
        input.addEventListener("input", () => runBase64Conversion(page));
        input.addEventListener("change", () => runBase64Conversion(page));
    });

    page.base64Input?.addEventListener("input", debounce(() => {
        if (page.mode === "base64-to-image") {
            runBase64Conversion(page);
        }
    }, 250));

    page.outputTabs.forEach(tab => {
        tab.addEventListener("click", () => {
            const target = tab.dataset.base64OutputTab;

            page.outputTabs.forEach(x => x.classList.toggle("active", x === tab));
            page.outputViews.forEach(view => {
                const active = view.dataset.base64OutputView === target;
                view.hidden = !active;
                view.classList.toggle("active", active);
            });
        });
    });
}

function setBase64Mode(page, mode) {
    page.mode = mode;

    page.modeTabs.forEach(tab => {
        tab.classList.toggle("active", tab.dataset.base64Mode === mode);
    });

    if (page.imagePanel) page.imagePanel.hidden = mode !== "image-to-base64";
    if (page.base64Panel) page.base64Panel.hidden = mode !== "base64-to-image";

    updateBase64Meta(page);
    runBase64Conversion(page);
}

async function addFiles(page, files) {
    const imageFiles = files.filter(file =>
        file.type.startsWith("image/") ||
        file.name.toLowerCase().endsWith(".svg")
    );

    const items = await Promise.all(imageFiles.map(async file => {
        const dataUrl = await readFileAsDataUrl(file);
        return {
            id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
            file,
            name: file.name,
            size: file.size,
            mime: file.type || detectMimeFromFileName(file.name),
            dataUrl,
            rawBase64: extractRawBase64(dataUrl),
            output: ""
        };
    }));

    base64Files.push(...items);
    renderFileList(page);
    runBase64Conversion(page);
}

function renderFileList(page) {
    if (!page.fileList) return;

    if (!base64Files.length) {
        page.fileList.innerHTML = "";
        return;
    }

    page.fileList.innerHTML = base64Files.map(item => `
        <article class="base64-file-row" data-file-id="${item.id}">
            <div class="base64-file-row__preview">
                <img src="${item.dataUrl}" alt="" />
            </div>
            <div class="base64-file-row__content">
                <strong>${escapeHtml(item.name)}</strong>
                <span>${formatBytes(item.size)} · ${escapeHtml(item.mime)}</span>
            </div>
            <button type="button" class="base64-file-row__remove" data-remove-file="${item.id}">
                <i class="fa-regular fa-xmark"></i>
            </button>
        </article>
    `).join("");

    page.fileList.querySelectorAll("[data-remove-file]").forEach(btn => {
        btn.addEventListener("click", () => {
            base64Files = base64Files.filter(x => x.id !== btn.dataset.removeFile);
            renderFileList(page);
            runBase64Conversion(page);
        });
    });
}

function runBase64Conversion(page) {
    if (page.mode === "image-to-base64") {
        runImageToBase64(page);
    } else {
        runBase64ToImage(page);
    }

    updateBase64Meta(page);
}

function runImageToBase64(page) {
    if (!base64Files.length) {
        setStatus(page, "No files", "");
        clearPreview(page);
        setOutput(page, "");
        renderBulkResults(page, []);
        return;
    }

    const results = base64Files.map(file => {
        const output = buildOutputByType(page, file.dataUrl, file.rawBase64, file.mime, file.name);
        file.output = output;

        return {
            id: file.id,
            name: file.name,
            mime: file.mime,
            size: file.size,
            dataUrl: file.dataUrl,
            output
        };
    });

    activeResult = results[0];

    setPreview(page, activeResult.dataUrl);
    setOutput(page, activeResult.output);
    renderBulkResults(page, results);
    setStatus(page, `${results.length} converted`, "is-valid");
}

function runBase64ToImage(page) {
    const raw = page.base64Input?.value?.trim() || "";

    if (!raw) {
        setStatus(page, "Empty", "");
        clearPreview(page);
        setOutput(page, "");
        renderBulkResults(page, []);
        return;
    }

    const parsed = parseBase64Image(raw, page.fallbackMime?.value || "image/png");

    if (!parsed.isValid) {
        setStatus(page, "Invalid Base64", "is-error");
        clearPreview(page);
        setOutput(page, "");
        return;
    }

    const fileName = normalizeFileName(page.fileName?.value || "decoded-image");
    const output = buildOutputByType(page, parsed.dataUrl, parsed.rawBase64, parsed.mime, fileName);

    activeResult = {
        id: "decoded",
        name: `${fileName}.${mimeToExtension(parsed.mime)}`,
        mime: parsed.mime,
        dataUrl: parsed.dataUrl,
        rawBase64: parsed.rawBase64,
        output
    };

    setPreview(page, parsed.dataUrl);
    setOutput(page, output);
    renderBulkResults(page, [activeResult]);
    setStatus(page, "Decoded", "is-valid");
}

function buildOutputByType(page, dataUrl, rawBase64, mime, fileName) {
    const outputType = page.outputType?.value || "data-url";
    const alt = escapeAttribute(page.alt?.value || "Base64 image");
    const width = Number(page.width?.value || 0);
    const height = Number(page.height?.value || 0);

    const src = page.includeMime?.checked
        ? dataUrl
        : rawBase64;

    const finalSrc = page.wrapLines?.checked
        ? wrapLongText(src)
        : src;

    switch (outputType) {
        case "img-tag": {
            const widthAttr = width > 0 ? ` width="${width}"` : "";
            const heightAttr = height > 0 ? ` height="${height}"` : "";
            return `<img src="${dataUrl}" alt="${alt}"${widthAttr}${heightAttr} />`;
        }

        case "raw-base64":
            return page.wrapLines?.checked ? wrapLongText(rawBase64) : rawBase64;

        case "css-background":
            return `background-image: url("${dataUrl}");`;

        case "data-url":
        default:
            return finalSrc;
    }
}

function parseBase64Image(input, fallbackMime) {
    const cleaned = input.trim().replace(/\s/g, "");

    const dataUrlMatch = cleaned.match(/^data:([^;]+);base64,(.+)$/i);

    if (dataUrlMatch) {
        const mime = dataUrlMatch[1];
        const rawBase64 = dataUrlMatch[2];

        return {
            isValid: isValidBase64(rawBase64),
            mime,
            rawBase64,
            dataUrl: `data:${mime};base64,${rawBase64}`
        };
    }

    const rawBase64 = cleaned.replace(/^base64,?/i, "");

    return {
        isValid: isValidBase64(rawBase64),
        mime: fallbackMime,
        rawBase64,
        dataUrl: `data:${fallbackMime};base64,${rawBase64}`
    };
}

function isValidBase64(value) {
    if (!value || value.length < 8) return false;

    try {
        atob(value);
        return true;
    } catch {
        return false;
    }
}

function setPreview(page, dataUrl) {
    if (!page.previewImage || !page.previewEmpty) return;

    page.previewImage.src = dataUrl;
    page.previewImage.hidden = false;
    page.previewEmpty.hidden = true;
}

function clearPreview(page) {
    if (page.previewImage) {
        page.previewImage.src = "";
        page.previewImage.hidden = true;
    }

    if (page.previewEmpty) {
        page.previewEmpty.hidden = false;
    }
}

function setOutput(page, value) {
    if (page.output) {
        page.output.value = value || "";
    }
}

function renderBulkResults(page, results) {
    if (!page.bulkGrid) return;

    if (!results.length) {
        page.bulkGrid.innerHTML = "";
        return;
    }

    page.bulkGrid.innerHTML = results.map(item => `
        <article class="base64-result-card" data-result-id="${item.id}">
            <div class="base64-result-card__preview">
                <img src="${item.dataUrl}" alt="" />
            </div>
            <div class="base64-result-card__content">
                <strong>${escapeHtml(item.name)}</strong>
                <span>${escapeHtml(item.mime || "")}</span>
            </div>
            <div class="base64-result-card__actions">
                <button type="button" class="btn btn-secondary btn-sm" data-copy-result="${item.id}">Copy</button>
                <button type="button" class="btn btn-ghost btn-sm" data-download-result="${item.id}">Download</button>
            </div>
        </article>
    `).join("");

    page.bulkGrid.querySelectorAll("[data-copy-result]").forEach(btn => {
        btn.addEventListener("click", async () => {
            const result = results.find(x => x.id === btn.dataset.copyResult);
            if (!result) return;

            await navigator.clipboard.writeText(result.output);
            setStatus(page, "Copied", "is-valid");
        });
    });

    page.bulkGrid.querySelectorAll("[data-download-result]").forEach(btn => {
        btn.addEventListener("click", () => {
            const result = results.find(x => x.id === btn.dataset.downloadResult);
            if (!result) return;

            downloadDataUrl(result.dataUrl, result.name || "image");
        });
    });
}

async function copyOutput(page) {
    const text = page.output?.value || "";
    if (!text) return;

    await navigator.clipboard.writeText(text);
    setStatus(page, "Copied", "is-valid");
}

function downloadActive(page) {
    if (!activeResult) return;

    if (page.mode === "base64-to-image") {
        downloadDataUrl(activeResult.dataUrl, activeResult.name);
        return;
    }

    const blob = new Blob([activeResult.output], { type: "text/plain;charset=utf-8" });
    downloadBlob(blob, `${normalizeFileName(activeResult.name)}.txt`);
}

async function downloadZip(page) {
    const zip = new JSZip();

    if (page.mode === "image-to-base64") {
        if (!base64Files.length) return;

        for (const file of base64Files) {
            zip.file(`${normalizeFileName(file.name)}.txt`, file.output || "");
        }
    } else if (activeResult) {
        const blob = dataUrlToBlob(activeResult.dataUrl);
        zip.file(activeResult.name, blob);
        zip.file(`${normalizeFileName(activeResult.name)}.txt`, activeResult.output || "");
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    downloadBlob(zipBlob, "base64-image-results.zip");
}

function loadBase64Sample(page) {
    if (!page.base64Input) return;

    page.base64Input.value =
        "data:image/svg+xml;base64," +
        btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180">
<rect width="320" height="180" rx="24" fill="#0f172a"/>
<circle cx="78" cy="90" r="38" fill="#6366f1"/>
<text x="135" y="98" font-family="Arial" font-size="24" fill="#fff">Viwo Tools</text>
</svg>`);

    runBase64Conversion(page);
}

function cleanBase64Input(page) {
    if (!page.base64Input) return;

    page.base64Input.value = page.base64Input.value.trim().replace(/\s/g, "");
    runBase64Conversion(page);
}

function clearBase64Tool(page) {
    base64Files = [];
    activeResult = null;

    if (page.fileInput) page.fileInput.value = "";
    if (page.base64Input) page.base64Input.value = "";
    if (page.output) page.output.value = "";

    renderFileList(page);
    renderBulkResults(page, []);
    clearPreview(page);
    updateBase64Meta(page);
    setStatus(page, "Ready", "");
}

function updateBase64Meta(page) {
    if (page.metaMode) {
        page.metaMode.textContent = page.mode === "image-to-base64"
            ? "Image to Base64"
            : "Base64 to Image";
    }

    if (page.metaFiles) {
        page.metaFiles.textContent = page.mode === "image-to-base64"
            ? String(base64Files.length)
            : activeResult ? "1" : "0";
    }

    if (page.metaSize) {
        if (page.mode === "image-to-base64" && base64Files.length) {
            const total = base64Files.reduce((sum, x) => sum + x.size, 0);
            page.metaSize.textContent = formatBytes(total);
        } else if (activeResult?.rawBase64) {
            page.metaSize.textContent = `${activeResult.rawBase64.length.toLocaleString()} chars`;
        } else {
            page.metaSize.textContent = "-";
        }
    }
}

function setStatus(page, text, stateClass = "") {
    if (!page.status) return;

    page.status.textContent = text;
    page.status.classList.remove("is-valid", "is-error");

    if (stateClass) {
        page.status.classList.add(stateClass);
    }
}

function readFileAsDataUrl(file) {
    return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => resolve("");
        reader.readAsDataURL(file);
    });
}

function extractRawBase64(dataUrl) {
    return String(dataUrl).split(",")[1] || "";
}

function detectMimeFromFileName(fileName) {
    const ext = fileName.split(".").pop()?.toLowerCase();

    switch (ext) {
        case "jpg":
        case "jpeg": return "image/jpeg";
        case "webp": return "image/webp";
        case "gif": return "image/gif";
        case "svg": return "image/svg+xml";
        case "png":
        default: return "image/png";
    }
}

function mimeToExtension(mime) {
    switch (mime) {
        case "image/jpeg": return "jpg";
        case "image/webp": return "webp";
        case "image/gif": return "gif";
        case "image/svg+xml": return "svg";
        case "image/png":
        default: return "png";
    }
}

function dataUrlToBlob(dataUrl) {
    const [meta, base64] = dataUrl.split(",");
    const mime = meta.match(/data:([^;]+);base64/i)?.[1] || "application/octet-stream";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: mime });
}

function downloadDataUrl(dataUrl, fileName) {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = fileName || "image";
    document.body.appendChild(a);
    a.click();
    a.remove();
}

function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 500);
}

function wrapLongText(value, size = 76) {
    return String(value).replace(new RegExp(`(.{1,${size}})`, "g"), "$1\n").trim();
}

function normalizeFileName(value) {
    return String(value || "file")
        .toLowerCase()
        .trim()
        .replace(/\.[a-z0-9]+$/i, "")
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "file";
}

function formatBytes(bytes) {
    if (!bytes) return "0 B";

    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);

    return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function escapeHtml(value) {
    return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function escapeAttribute(value) {
    return escapeHtml(value).replaceAll("`", "&#96;");
}

function debounce(fn, delay = 150) {
    let timer = null;

    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}